const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

class CountdownTimer extends HTMLElement {
    connectedCallback() {
        this.endAttr = this.getAttribute("data-countdown-end");
        this.expiredSetting = this.getAttribute("data-expired-setting") || "remove";
        this.removeScope = this.getAttribute("data-remove-scope") || "element";
        this.durationHours = Number(this.getAttribute("data-duration-hours")) || 0;

        this.valueEls = {
            days: this.querySelector('[data-value="days"]'),
            hours: this.querySelector('[data-value="hours"]'),
            minutes: this.querySelector('[data-value="minutes"]'),
            seconds: this.querySelector('[data-value="seconds"]'),
        };

        this.unitEls = {
            days: this.querySelector('[data-unit="days"]'),
        };

        this.start();
    }

    start() {
        const now = new Date();
        const endDate = this.endAttr ? new Date(this.endAttr) : null;

        if (!endDate || Number.isNaN(endDate.getTime()) || endDate <= now) {
            this.handleExpired(now);
            return;
        }

        this.tick(endDate);
        this.interval = setInterval(() => this.tick(endDate), 1000);
    }

    handleExpired(now) {
        if (this.durationHours > 0) {
            const blockEnd = this.createBlockEnd(now);
            this.updateDeadlineTargets(blockEnd);
            this.tick(blockEnd);
            this.interval = setInterval(() => this.tick(blockEnd), 1000);
            return;
        }

        if (this.expiredSetting === "midnight") {
            const midnight = new Date(now);
            midnight.setHours(23, 59, 59, 999);

            if (midnight > now) {
                this.tick(midnight);
                this.interval = setInterval(() => this.tick(midnight), 1000);
                return;
            }
        }

        this.removeByScope();
    }

    /*
        Countdown length set (data-duration-hours): the visitor's day splits into
        blocks of that length starting at local midnight (1.5 -> 1:30, 3:00, 4:30 ...)
        and the timer counts to the end of the current block. A length that doesn't
        divide 24 leaves a shorter last block that ends at midnight; 24 or more just
        counts to midnight. Blocks follow the clock, so on daylight-saving days the
        block spanning the change runs an hour shorter or longer.
    */
    createBlockEnd(now) {
        const blockMs = this.durationHours * 3600000;
        const year = now.getFullYear();
        const month = now.getMonth();
        const day = now.getDate();
        const nextMidnight = new Date(year, month, day + 1);
        const clockMs = ((now.getHours() * 60 + now.getMinutes()) * 60 + now.getSeconds()) * 1000 + now.getMilliseconds();

        let boundaryMs = (Math.floor(clockMs / blockMs) + 1) * blockMs;
        let blockEnd = new Date(year, month, day, 0, 0, 0, boundaryMs);

        // When clocks fall back, a boundary in the repeated hour can resolve to the
        // first pass of that hour, which has already gone; step to the next one.
        while (blockEnd <= now && blockEnd < nextMidnight) {
            boundaryMs += blockMs;
            blockEnd = new Date(year, month, day, 0, 0, 0, boundaryMs);
        }

        return blockEnd < nextMidnight ? blockEnd : nextMidnight;
    }

    /*
        The page's "Order by" dates (data-content="countdown-deadline") follow the
        current block, rewritten each time a new one starts. They show the block's
        last minute, the way the midnight countdown's date reads 11:59pm (a block
        ending at 3:00 reads 02:59pm), in the strftime pattern Liquid rendered them
        with (data-date-format), so the text matches the server-rendered one.
    */
    updateDeadlineTargets(blockEnd) {
        const lastMoment = new Date(blockEnd.getTime() - 1);

        document.querySelectorAll('[data-content="countdown-deadline"]').forEach((el) => {
            const format = el.getAttribute("data-date-format");
            if (format) el.textContent = this.createDeadlineText(lastMoment, format);
            el.setAttribute("data-state", "ready");
        });
    }

    // Covers the strftime tokens the "Order by" lines use: %b %B %d %I %M %P.
    createDeadlineText(date, format) {
        const month = MONTHS[date.getMonth()];
        const hours = date.getHours();
        const parts = {
            b: month.slice(0, 3),
            B: month,
            d: String(date.getDate()).padStart(2, "0"),
            I: String(hours % 12 || 12).padStart(2, "0"),
            M: String(date.getMinutes()).padStart(2, "0"),
            P: hours < 12 ? "am" : "pm",
        };

        return format.replace(/%([bBdIMP])/g, (token, key) => parts[key]);
    }

    tick(endDate) {
        const diffMs = endDate - new Date();

        if (diffMs <= 0) {
            clearInterval(this.interval);
            this.handleExpired(new Date());
            return;
        }

        const totalSeconds = Math.floor(diffMs / 1000);

        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        this.setUnit("days", days, { pad: false });
        this.setUnit("hours", hours, { pad: true });
        this.setUnit("minutes", minutes, { pad: true });
        this.setUnit("seconds", seconds, { pad: true });

        // Days conditional visibility
        if (this.unitEls.days) this.unitEls.days.classList.toggle('hidden', days <= 0)

    }

    setUnit(name, value, opts = {}) {
        const el = this.valueEls[name];
        if (!el) return;

        const str = opts.pad ? String(value).padStart(2, "0") : String(value);
        el.textContent = str;
    }

    removeByScope() {
        clearInterval(this.interval);

        if (this.removeScope === "parent") {
            const wrapper = this.closest("[data-countdown-wrapper]");
            if (wrapper) {
                wrapper.remove();
                return;
            }
        }

        this.remove();
    }

    disconnectedCallback() {
        clearInterval(this.interval);
    }
}

customElements.define("countdown-timer", CountdownTimer);
